import clsx from "clsx";
import { addMonths, format, subMonths } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { Button, LoadPanel, Pagination, TextBox } from "devextreme-react";
import { Toolbar, Item as ToolbarItem } from "devextreme-react/toolbar";
import _ from "lodash";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AnnualLeaveEntry } from "../../../api/calendarData";
import { useAuth } from "../../../contexts/auth";
import {
  EmploymentIndex,
  employmentKey,
  isDayWithinEmployment,
} from "../../../lib/employment";
import { ThemeContext } from "../../../theme/theme";
import { useScreenSize } from "../../../utils/media-query";
import { ALLOWED_PAGE_SIZES } from "../constants";
import type { AbsenceData2 } from "../types";
import {
  getBorrowedBlockStatus,
  getCellClassAndIcon,
} from "../utils/absenceClassHelpers";

interface AllEmployeesTableView2Props {
  currentMonth: Date;
  setCurrentMonth: (date: Date) => void;
  entries: AnnualLeaveEntry[];
  totalCount: number;
  pageIndex: number;
  setPageIndex: (idx: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  gridSearchQuery: string;
  setGridSearchQuery: (q: string) => void;
  holidayData: Record<number, AbsenceData2[]>;
  isLoading: boolean;
  isForeman: boolean;
  isHR: boolean;
  onCellDoubleClick: (date: Date, employeeId: number, companyId: number) => void;
  lastCellClickTime: React.MutableRefObject<number>;
  employmentIndex: EmploymentIndex;
}

export function AllEmployeesTableView2({
  currentMonth,
  setCurrentMonth,
  entries,
  totalCount,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  gridSearchQuery,
  setGridSearchQuery,
  holidayData,
  isLoading,
  isForeman,
  isHR,
  onCellDoubleClick,
  lastCellClickTime,
  employmentIndex,
}: AllEmployeesTableView2Props) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const themeContext = useContext(ThemeContext);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const translatedMonth = t(`months.${format(currentMonth, "MMMM")}`);
  const [gridSearchInput, setGridSearchInput] = useState("");
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<number | null>(
    null,
  );

  // Pre-compute borrowed block per employee (once per render, not per cell)
  const borrowedBlockMap = useMemo(() => {
    const map = new Map<
      number,
      { isBorrowed: boolean; isMyBorrowedEmployee: boolean }
    >();
    for (const entry of entries) {
      const emp = entry.employee;
      if (!emp.isBorrowed) {
        map.set(emp.id, { isBorrowed: false, isMyBorrowedEmployee: false });
        continue;
      }
      const isMyBorrowed =
        emp.borrowedByManagerId === user?.employeeId &&
        emp.borrowedByManagerCompanyId === user?.companyId;
      map.set(emp.id, { isBorrowed: true, isMyBorrowedEmployee: isMyBorrowed });
    }
    return map;
  }, [entries, user?.employeeId, user?.companyId]);

  const changeMonth = (direction: "next" | "previous") => {
    setCurrentMonth(
      direction === "next"
        ? addMonths(currentMonth, 1)
        : subMonths(currentMonth, 1),
    );
  };

  const renderMonthNav = () => (
    <div className="flex items-center gap-1">
      <Button
        icon="chevronprev"
        stylingMode="text"
        onClick={() => changeMonth("previous")}
      />
      <div
        className={clsx(
          "text-center text-base",
          isMobile ? "flex-1 font-semibold" : "w-32",
        )}
      >
        {`${translatedMonth} ${format(currentMonth, "yyyy")}`}
      </div>
      <Button
        icon="chevronnext"
        stylingMode="text"
        onClick={() => changeMonth("next")}
      />
    </div>
  );

  const renderSearch = () => (
    <TextBox
      value={gridSearchInput}
      width={isMobile ? "100%" : undefined}
      placeholder={t("holidayPopup.Searchbyname")}
      onValueChange={(value) => setGridSearchInput(value)}
      onFocusOut={() => {
        setGridSearchQuery(gridSearchInput);
        setPageIndex(1);
      }}
      onEnterKey={() => {
        setGridSearchQuery(gridSearchInput);
        setPageIndex(1);
      }}
    />
  );

  const emptyState = (
    <div
      className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
      role="alert"
    >
      {gridSearchQuery !== ""
        ? t("common.noSearchResults")
        : t("noSubordinateEmployees")}
    </div>
  );

  return (
    <div>
      {isMobile ? (
        <div
          className={clsx(
            "sticky top-0 z-10 flex flex-col gap-2 pb-3 mb-2",
            themeContext?.theme === "dark" ? "bg-[#1e1e1e]" : "bg-white",
          )}
        >
          {renderMonthNav()}
          {renderSearch()}
        </div>
      ) : (
        <Toolbar className="mb-2">
          <ToolbarItem location="before" locateInMenu="auto">
            {renderMonthNav()}
          </ToolbarItem>
          <ToolbarItem location="after" locateInMenu="auto">
            {renderSearch()}
          </ToolbarItem>
        </Toolbar>
      )}

      <LoadPanel
        shading
        shadingColor="rgba(0, 0, 0, 0.3)"
        visible={isLoading}
      />

      {!isLoading && entries.length === 0 ? (
        emptyState
      ) : isMobile ? (
        <>
          <div className="flex flex-col gap-3 pb-4">
            {entries.map((entry) => {
              const expanded = expandedEmployeeId === entry.employee.id;
              return (
                <div
                  key={entry.employee.id}
                  className={clsx(
                    "rounded-lg border shadow-sm overflow-hidden",
                    themeContext?.theme === "dark"
                      ? "bg-[#1e1e1e] border-gray-700"
                      : "bg-white border-gray-200",
                  )}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedEmployeeId((prev) =>
                        prev === entry.employee.id ? null : entry.employee.id,
                      )
                    }
                    className={clsx(
                      "w-full text-left p-3 flex items-start gap-2",
                      themeContext?.theme === "dark"
                        ? "hover:bg-[#2a2a2a]"
                        : "hover:bg-gray-50",
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">
                        {`${entry.employee.lastName} ${entry.employee.firstName}`}
                      </div>
                      <div className="text-xs opacity-90 mt-1 break-words">
                        {t("holiday.planned", {
                          days:
                            entry.sum?.sumaDni ??
                            entry.summary?.sumaGodzin ??
                            0,
                          hours:
                            entry.sum?.sumaGodzin ??
                            entry.summary?.sumaGodzin ??
                            0,
                        })}
                      </div>
                      {entry.remainingAnnualLeave !== undefined ? (
                        entry.remainingAnnualLeave !== null ? (
                          <div className="text-xs opacity-90 break-words">
                            {t("holiday.remaining", {
                              hours: entry.remainingAnnualLeave.hours,
                              days: entry.remainingAnnualLeave.days,
                            })}
                          </div>
                        ) : (
                          <div className="text-xs opacity-90 break-words">
                            {t("holiday.notDefined")}
                          </div>
                        )
                      ) : null}
                    </div>
                    <i
                      className={clsx(
                        "dx-icon text-xl mt-1",
                        expanded
                          ? "dx-icon-chevronup"
                          : "dx-icon-chevrondown",
                      )}
                    />
                  </button>
                  {expanded ? (
                    <div className="flex flex-col gap-2 p-3 pt-0">
                      {entry.days.map((day) => {
                        const formattedDate = new Date(
                          day.date,
                        ).toLocaleDateString("en-CA");
                        const dayType = day.dayType ?? null;
                        const { changeClass, icon, absenceDataForCell } =
                          getCellClassAndIcon(
                            formattedDate,
                            themeContext?.theme,
                            entry.employee.id,
                            holidayData,
                            dayType,
                          );
                        const {
                          isBorrowedAndBlocked,
                          isMyBorrowedEmployee,
                        } = getBorrowedBlockStatus(
                          entry.employee,
                          day.date,
                          user?.employeeId,
                          user?.companyId,
                          isHR,
                        );
                        const dateObj = new Date(day.date);
                        const dayShort = _.upperFirst(
                          format(dateObj, "EEEEEE", {
                            locale: i18n.language === "en" ? enUS : pl,
                          }),
                        );
                        const dayFull = _.upperFirst(
                          format(dateObj, "EEEE, d.LL", {
                            locale: i18n.language === "en" ? enUS : pl,
                          }),
                        );
                        const outsideContract = !isDayWithinEmployment(
                          employmentIndex.get(
                            employmentKey(
                              entry.employee.companyId,
                              entry.employee.id,
                            ),
                          ),
                          day.date,
                        );
                        const disabled =
                          isForeman ||
                          isBorrowedAndBlocked ||
                          outsideContract;

                        return (
                          <div
                            key={day.date}
                            className={clsx(
                              "flex items-stretch gap-3 p-3 rounded-lg border-2 border-transparent",
                              changeClass,
                              disabled
                                ? "opacity-70 cursor-not-allowed"
                                : "cursor-pointer",
                            )}
                            style={
                              isBorrowedAndBlocked
                                ? {
                                    backgroundColor:
                                      "rgba(206, 114, 114, 0.34)",
                                  }
                                : undefined
                            }
                            title={
                              outsideContract
                                ? t("holidayPopup.outsideEmployment")
                                : isBorrowedAndBlocked
                                  ? isMyBorrowedEmployee
                                    ? t("holidayPopup.cannotEditOutsideRental")
                                    : t("holidayPopup.cannotEditDuringRental")
                                  : ""
                            }
                            onClick={
                              disabled
                                ? undefined
                                : () =>
                                    onCellDoubleClick(
                                      dateObj,
                                      entry.employee.id,
                                      entry.employee.companyId,
                                    )
                            }
                          >
                            <div className="flex flex-col items-center justify-center min-w-[52px] font-bold">
                              <div className="text-[11px] uppercase opacity-80">
                                {dayShort}
                              </div>
                              <div className="text-2xl leading-none mt-1">
                                {format(dateObj, "d")}
                              </div>
                            </div>
                            <div className="flex-1 flex flex-col justify-center text-sm min-w-0">
                              <div className="text-[11px] opacity-80">
                                {dayFull}
                              </div>
                              {absenceDataForCell ? (
                                <>
                                  <div className="font-medium break-words">
                                    {absenceDataForCell.name}
                                  </div>
                                  {absenceDataForCell.note ? (
                                    <div className="text-xs opacity-85 break-words">
                                      {absenceDataForCell.note}
                                    </div>
                                  ) : null}
                                </>
                              ) : day.hours ? (
                                <div className="break-words">
                                  {`${day.hours.fromHour ?? ""} - ${day.hours.toHour ?? ""}`}
                                  {day.hours.duration ? (
                                    <span className="opacity-70 ml-1">
                                      {`(${day.hours.duration})`}
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            {icon ? (
                              <div className="flex items-center text-xl">
                                {icon}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <Pagination
            itemCount={totalCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            showInfo
            showPageSizeSelector
            showNavigationButtons
            onPageIndexChange={setPageIndex}
            onPageSizeChange={setPageSize}
            allowedPageSizes={[...ALLOWED_PAGE_SIZES]}
          />
        </>
      ) : (
        <div>
          <div
            className={clsx(
              "table-wrapper",
              themeContext?.theme === "dark" ? "dark" : null,
            )}
            style={{ maxHeight: "60vh" }}
          >
            <table>
              <thead>
                <tr
                  className="sticky"
                  style={{
                    top: 0,
                    zIndex: 3,
                    backgroundColor:
                      themeContext?.theme === "dark" ? "#1e1e1e" : "#f9f9f9",
                  }}
                >
                  <th
                    className="sticky"
                    style={{
                      left: 0,
                      backgroundColor:
                        themeContext?.theme === "dark" ? "#1e1e1e" : "#f9f9f9",
                      zIndex: 4,
                    }}
                  >
                    <div className="box-dark w-32 sm:w-60">{t("Pracownik")}</div>
                  </th>
                  {entries[0]?.days?.map((day) => (
                    <th
                      key={day.date}
                      style={{
                        backgroundColor:
                          themeContext?.theme === "dark"
                            ? "#1e1e1e"
                            : "#f9f9f9",
                      }}
                    >
                      <div
                        className="box-dark"
                        style={{ minWidth: 110, width: "100%" }}
                      >
                        {_.upperFirst(
                          format(day.date, "EEEEEE, d.LL", {
                            locale: i18n.language === "en" ? enUS : pl,
                          }),
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.employee.id}>
                    <td
                      className="sticky"
                      style={{
                        height: "90px",
                        backgroundColor:
                          themeContext?.theme === "dark"
                            ? "#1e1e1e"
                            : "#f9f9f9",
                      }}
                    >
                      <div className="box-dark w-32 sm:w-60 h-full">
                        <div className="font-bold text-wrap truncate">
                          {`${entry.employee.lastName} ${entry.employee.firstName}`}
                        </div>
                        <div className="text-wrap truncate">
                          {t("holiday.planned", {
                            days:
                              entry.sum?.sumaDni ??
                              entry.summary?.sumaGodzin ??
                              0,
                            hours:
                              entry.sum?.sumaGodzin ??
                              entry.summary?.sumaGodzin ??
                              0,
                          })}
                        </div>
                        {entry.remainingAnnualLeave !== undefined ? (
                          entry.remainingAnnualLeave !== null ? (
                            <div className="text-wrap truncate">
                              {t("holiday.remaining", {
                                hours: entry.remainingAnnualLeave.hours,
                                days: entry.remainingAnnualLeave.days,
                              })}
                            </div>
                          ) : (
                            <div className="text-wrap truncate">
                              {t("holiday.notDefined")}
                            </div>
                          )
                        ) : null}
                      </div>
                    </td>
                    {entry.days.map((day) => {
                      const formattedDate = new Date(
                        day.date,
                      ).toLocaleDateString("en-CA");
                      const dayType = day.dayType ?? null;
                      const { changeClass, icon, absenceDataForCell } =
                        getCellClassAndIcon(
                          formattedDate,
                          themeContext?.theme,
                          entry.employee.id,
                          holidayData,
                          dayType,
                        );

                      const { isBorrowedAndBlocked, isMyBorrowedEmployee } =
                        getBorrowedBlockStatus(
                          entry.employee,
                          day.date,
                          user?.employeeId,
                          user?.companyId,
                          isHR,
                        );

                      const outsideContract = !isDayWithinEmployment(
                        employmentIndex.get(
                          employmentKey(
                            entry.employee.companyId,
                            entry.employee.id,
                          ),
                        ),
                        day.date,
                      );

                      return (
                        <td key={day.date} style={{ height: "90px" }}>
                          <div
                            className={clsx(
                              "box-light",
                              changeClass,
                              (isBorrowedAndBlocked || outsideContract) &&
                                "cursor-not-allowed pointer-events-none",
                              outsideContract && "opacity-50",
                            )}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                              backgroundColor: isBorrowedAndBlocked
                                ? "rgba(206, 114, 114, 0.34)"
                                : undefined,
                              touchAction: "manipulation",
                              userSelect: "none",
                            }}
                            onClick={() => {
                              if (
                                isForeman ||
                                isBorrowedAndBlocked ||
                                outsideContract
                              )
                                return;
                              const now = Date.now();
                              if (now - lastCellClickTime.current < 300) {
                                lastCellClickTime.current = 0;
                                onCellDoubleClick(
                                  new Date(day.date),
                                  entry.employee.id,
                                  entry.employee.companyId,
                                );
                              } else {
                                lastCellClickTime.current = now;
                              }
                            }}
                            title={
                              outsideContract
                                ? t("holidayPopup.outsideEmployment")
                                : isBorrowedAndBlocked
                                  ? isMyBorrowedEmployee
                                    ? t("holidayPopup.cannotEditOutsideRental")
                                    : t("holidayPopup.cannotEditDuringRental")
                                  : ""
                            }
                          >
                            <div>
                              {absenceDataForCell
                                ? "-"
                                : day.hours
                                  ? `${day.hours.fromHour ?? ""} - ${day.hours.toHour ?? ""}`
                                  : "-"}
                            </div>
                            {!absenceDataForCell && day.hours?.duration && (
                              <div>{`(${day.hours.duration})`}</div>
                            )}
                            {icon && <div style={{ marginTop: 4 }}>{icon}</div>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            itemCount={totalCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            showInfo
            showPageSizeSelector
            showNavigationButtons
            onPageIndexChange={setPageIndex}
            onPageSizeChange={setPageSize}
            allowedPageSizes={[...ALLOWED_PAGE_SIZES]}
          />
        </div>
      )}
    </div>
  );
}
